import { Injectable, signal, inject } from '@angular/core';
import { Mp3Encoder } from '@breezystack/lamejs';
import { DifyConfigService } from '@/app/services/dify-config.service';

/**
 * 语音服务。
 *
 * 统一处理：
 * - 浏览器录音（MediaRecorder，webm）
 * - 将 webm 转码为 mp3（AudioContext 解码 PCM + lamejs 编码）
 * - 调用 Dify `/audio-to-text` 语音转文字（STT）
 * - 调用 Dify `/text-to-audio` 文字转语音（TTS）并播放
 *
 * POC 阶段使用浏览器原生录音能力；真实环境可替换为专业语音 SDK。
 */
@Injectable({ providedIn: 'root' })
export class AudioService {
  private readonly configService = inject(DifyConfigService);

  /** apiUrl / apiKey 实时读取当前目标（公司/本地），与语音报报告一致 */
  private get apiUrl(): string {
    return this.configService.voiceReportConfig.apiUrl;
  }
  private get apiKey(): string {
    return this.configService.voiceReportConfig.apiKey;
  }

  private mediaRecorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;

  /** 是否正在录音 */
  isRecording = signal(false);
  /** 是否正在转文字 */
  isTranscribing = signal(false);
  /** 是否正在语音播报 */
  isSpeaking = signal(false);
  /** 转文字错误信息 */
  transcribeError = signal<string | null>(null);

  /** 浏览器是否支持语音输入 */
  get supported(): boolean {
    return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;
  }

  /**
   * 开始录音。
   * @throws 浏览器不支持或用户拒绝授权时抛出
   */
  async startRecording(): Promise<void> {
    if (!this.supported) {
      throw new Error('当前浏览器不支持语音输入');
    }
    if (this.isRecording()) return;

    this.transcribeError.set(null);
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mediaRecorder = new MediaRecorder(this.stream);
    this.chunks = [];

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };

    this.mediaRecorder.start();
    this.isRecording.set(true);
  }

  /**
   * 停止录音，返回 mp3 Blob。
   * @returns mp3 Blob（若转码失败则返回原始 webm）
   */
  async stopRecording(): Promise<Blob> {
    return new Promise<Blob>((resolve) => {
      if (!this.mediaRecorder || !this.isRecording()) {
        resolve(new Blob());
        return;
      }

      this.mediaRecorder.onstop = async () => {
        this.isRecording.set(false);
        const webmBlob = new Blob(this.chunks, { type: this.mediaRecorder!.mimeType });
        this.cleanup();
        try {
          const mp3 = await this.convertToMp3(webmBlob);
          resolve(mp3);
        } catch (err: any) {
          this.transcribeError.set(`音频格式转换失败: ${err?.message || '未知错误'}`);
          resolve(webmBlob);
        }
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * 取消录音（不转码，丢弃数据）。
   */
  cancelRecording(): void {
    if (this.mediaRecorder && this.isRecording()) {
      this.mediaRecorder.stop();
      this.isRecording.set(false);
    }
    this.cleanup();
  }

  /**
   * 将 webm 音频转码为 mp3。
   * 链路：webm Blob -> AudioContext 解码为 PCM(Float32) -> Int16 -> lamejs 编码 -> mp3 Blob
   */
  private async convertToMp3(webmBlob: Blob): Promise<Blob> {
    if (!Mp3Encoder || !webmBlob.size) {
      throw new Error('mp3 编码器不可用');
    }

    // 兼容 Safari（webkitAudioContext）
    const AC =
      window.AudioContext || (window as any).webkitAudioContext || (window as any).mozAudioContext;
    this.audioContext = this.audioContext || new AC();
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    const pcmData = this.audioBufferToPcm(audioBuffer);

    // 16kHz 单声道编码（实测 Dify STT 对 24kHz 立体声识别为空，16kHz 单声道可正常识别）
    const sampleRate = 16000;
    const channels = 1;
    const mp3encoder = new Mp3Encoder(channels, sampleRate, 128);

    // 分块编码
    const blockSize = 1152;
    const mp3Data: Uint8Array[] = [];
    const samples = pcmData;
    for (let i = 0; i < samples.length; i += blockSize) {
      const block = samples.subarray(i, i + blockSize);
      const mp3buf = mp3encoder.encodeBuffer(block);
      if (mp3buf && mp3buf.length > 0) mp3Data.push(new Uint8Array(mp3buf));
    }
    const end = mp3encoder.flush();
    if (end && end.length > 0) mp3Data.push(new Uint8Array(end));

    const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' });
    return mp3Blob;
  }

  /** 将 AudioBuffer 转为 Int16 PCM 数组（下采样到 16kHz 单声道） */
  private audioBufferToPcm(buffer: AudioBuffer): Int16Array {
    const targetRate = 16000;
    const targetLen = Math.round(buffer.length * (targetRate / buffer.sampleRate));
    const out = new Int16Array(targetLen);

    // 混合所有声道到单声道
    const numChannels = buffer.numberOfChannels;
    const ratio = buffer.sampleRate / targetRate;
    for (let i = 0; i < targetLen; i++) {
      const srcIdx = Math.min(Math.floor(i * ratio), buffer.length - 1);
      let sum = 0;
      for (let c = 0; c < numChannels; c++) {
        sum += buffer.getChannelData(c)[srcIdx];
      }
      const v = sum / numChannels;
      out[i] = Math.max(-1, Math.min(1, v)) * 0x7fff;
    }
    return out;
  }

  /**
   * 语音转文字（Dify /audio-to-text）。
   * @returns 识别出的文字
   */
  async transcribe(audioBlob: Blob): Promise<string> {
    if (!audioBlob || !audioBlob.size) return '';
    this.isTranscribing.set(true);
    this.transcribeError.set(null);
    try {
      // 统一用 audio/mp3 的 MIME 与 .mp3 后缀（Dify 不接受 audio/mpeg；若内容为 webm 则 Dify 会返回 415）
      const fileBlob = new Blob([audioBlob], { type: 'audio/mp3' });
      const formData = new FormData();
      formData.append('file', fileBlob, 'voice.mp3');
      formData.append('user', 'tvms-voice-user');

      const response = await fetch(`${this.apiUrl}/audio-to-text`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.apiKey}` },
        body: formData,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`语音转文字失败: HTTP ${response.status} ${errText}`);
      }
      const data = await response.json();
      return data.text || '';
    } catch (err: any) {
      this.transcribeError.set(err.message || '语音转文字失败');
      return '';
    } finally {
      this.isTranscribing.set(false);
    }
  }

  /**
   * 文字转语音（Dify /text-to-audio）并播放。
   * @param text 要播报的文字
   * @param voice 声音（可选，默认用空让服务端决定）
   */
  async speak(text: string, voice?: string): Promise<void> {
    if (!text) return;
    this.isSpeaking.set(true);
    try {
      const response = await fetch(`${this.apiUrl}/text-to-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          text,
          user: 'tvms-voice-user',
          ...(voice ? { voice } : {}),
        }),
      });
      if (!response.ok) return;

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
    } catch {
      /* 播报失败不影响主流程 */
    } finally {
      this.isSpeaking.set(false);
    }
  }

  /** 停止播报 */
  stopSpeaking(): void {
    // 简单实现：通过中断当前 audio（后续可优化）
  }

  /** 清理录音资源 */
  private cleanup(): void {
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}
